/** @jsxImportSource @emotion/react */
import { t } from "i18next";
import { useContext, useState } from "react";
import { LanguageContext } from "../../contexts/LanguageCtx";
import useBreakpoint from "../../hooks/useBreakpoint";
import { theme } from "../../theme/theme";
import { videos, videos_DE, videos_EN } from "../../theme/videos";
import { CategoryType } from "../../types/types";
import Box from "../Box/Box";
import Button from "../Button/Button";
import DisplayContentLayout from "../Layout/DisplayContentLayout";
import Stepper from "../Stepper/Stepper";
import Video from "../Video/Video";
import Typography from "../Typography/Typography";
import CopyRight from "../CopyRight/CopyRight";

type VideoHeadlineDisplayProps = {
  imageUrl: string;
  categoryTitle: string;
  description: string;
  showVideo: boolean;
  handlePlayVideo?: () => void;
  onClose: () => void;
  slideId: number;
  copyRightLabel: string;
};
const VideoHeadlineDisplay = ({
  imageUrl,
  categoryTitle,
  description,
  onClose,
  slideId,
  copyRightLabel,
}: VideoHeadlineDisplayProps) => {
  const { selectedLanguage } = useContext(LanguageContext);
  const { isMobile, isTablet, isLandscape } = useBreakpoint();
  const [imgOpacity, setImgOpacity] = useState(0);
  const [videoState, setVideoState] = useState({
    isVideoPlaying: false,
    isVideoZoomIn: false,
  });

  const getVideoByCategory = (cat: CategoryType) => {
    if (selectedLanguage === "en") {
      if (cat === "Special3DConcretePrinting")
        return videos_EN.Special3DConcretePrinting_EN;
    }
    if (selectedLanguage === "de") {
      if (cat === "Special3DConcretePrinting")
        return videos_DE.Special3DConcretePrinting_DE;
    }
  };

  return (
    <DisplayContentLayout
      imgOpacity={imgOpacity}
      handleClick={onClose}
      slideId={slideId}
    >
      {isMobile && (
        <Box>
          {videoState.isVideoPlaying && (
            <Video
              isVideoOpen={videoState.isVideoPlaying}
              isVideoZoomIn={videoState.isVideoZoomIn}
              handleZoomIn={() =>
                setVideoState({ ...videoState, isVideoZoomIn: true })
              }
              handleClose={() =>
                setVideoState({
                  isVideoPlaying: false,
                  isVideoZoomIn: false,
                })
              }
              videoUrl={
                selectedLanguage === "de"
                  ? videos.Special3DConcretePrinting_DE
                  : videos.Special3DConcretePrinting_EN
              }
            />
          )}
          <Box
            height="100%"
            width="100vw"
            padding={30}
            marginTop={isMobile ? "13vh" : undefined}
          >
            <Box position="relative">
              <img
                css={{
                  width: "100%",
                  height: 240,
                  objectFit: "cover",
                  borderBottomRightRadius: "0px",
                }}
                src={imageUrl}
                alt="Heidelberg image"
                onLoad={() => setImgOpacity(1)}
              />
              <CopyRight copyRightLabel={copyRightLabel} />
              {!videoState.isVideoPlaying && (
                <Button
                  style={{
                    position: "absolute",
                    bottom: -10,
                    right: 0,
                    zIndex: 12,
                  }}
                  label={t(`heidelberg.buttons.play`)}
                  onClick={() =>
                    setVideoState({
                      isVideoZoomIn: false,
                      isVideoPlaying: true,
                    })
                  }
                  type="play"
                />
              )}
            </Box>

            <Typography
              variant={isMobile ? "h2_mobile" : "h2"}
              marginTop={16}
              marginBottom={16}
              maxWidth={250}
              css={{
                color: theme.colors.primary,
                fontSize: isMobile ? 16 : undefined,
              }}
            >
              {categoryTitle}
            </Typography>

            <Typography
              variant={isMobile ? "body1_mobile" : "body1"}
              color="primary"
            >
              {description}
            </Typography>
          </Box>

          <Box display="flex" justifyContent="flex-end" marginRight={30}>
            <Stepper slideId={slideId} />
          </Box>
        </Box>
      )}

      {!isMobile && (
        <Box>
          <Box display="flex" position="relative">
            <Box
              width={460}
              height={260}
              position={videoState.isVideoZoomIn ? undefined : "relative"}
            >
              <img
                css={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  borderTopLeftRadius: videoState.isVideoPlaying ? 0 : "190px",
                  opacity: videoState.isVideoPlaying ? 0 : 1,
                  transition: "all 1s ease-out",
                }}
                onLoad={() => setImgOpacity(1)}
                src={imageUrl}
              />
              <CopyRight copyRightLabel={copyRightLabel} />
              {
                <Box
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    opacity: !videoState.isVideoPlaying ? 0 : 1,
                    transition: "all 2s ease-out",
                  }}
                >
                  <Video
                    isVideoOpen={videoState.isVideoPlaying}
                    isVideoZoomIn={videoState.isVideoZoomIn}
                    handleZoomIn={() =>
                      setVideoState({ ...videoState, isVideoZoomIn: true })
                    }
                    handleClose={() =>
                      setVideoState({
                        isVideoPlaying: false,
                        isVideoZoomIn: false,
                      })
                    }
                    videoUrl={
                      selectedLanguage === "de"
                        ? videos.Special3DConcretePrinting_DE
                        : videos.Special3DConcretePrinting_EN
                    }
                  />
                </Box>
              }

              {!videoState.isVideoPlaying && (
                <Button
                  style={{
                    position: "absolute",
                    bottom: -10,
                    right: 0,
                    zIndex: 12,
                  }}
                  label={t(`heidelberg.buttons.play`)}
                  onClick={() =>
                    setVideoState({ ...videoState, isVideoPlaying: true })
                  }
                  type="play"
                />
              )}
            </Box>

            <Box
              style={{
                width: 580,
                height: 300,
                backgroundColor: theme.colors.primary,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                borderBottomRightRadius: "190px",
                marginTop: 100,
                marginLeft: -100,
                zIndex: -1,
              }}
            >
              <Typography
                variant="h2"
                css={{ maxWidth: 300, color: "white", paddingLeft: 100 }}
              >
                {categoryTitle}
              </Typography>
            </Box>
          </Box>

          <Box
            display="flex"
            marginLeft={isTablet || isLandscape ? undefined : 100}
          >
            <Box
              width={290}
              height={330}
              display="flex"
              justifyContent="flex-end"
              alignItems="flex-end"
            >
              <Box
                width="80%"
                height="100%"
                backgroundColor={theme.colors.red}
                borderBottomLeftRadius="190px"
                marginBottom={150}
                zIndex={-2}
              ></Box>
            </Box>

            <Box width={500} height={400} padding={12}>
              <Typography variant="body1" color="primary">
                  {description}
                </Typography>
              </Box>
          </Box>
          <Box position="absolute" right={0} paddingBottom={30}>
            {isTablet || isLandscape ? <Stepper slideId={slideId} /> : <></>}
          </Box>
        </Box>
      )}
    </DisplayContentLayout>
  );
};

export default VideoHeadlineDisplay;
